import { useState } from 'react';
import {
  AccountModel,
  DiaChi,
  DonViCoSo,
  HanThanhToan,
  KhachHang,
  NgoaiTe,
  NhanVien,
  NhapXuat,
  PhuongThucGiaoHang,
  PhuongThucThanhToan,
  PhuongTienVanChuyen,
  QuyenChungTu
} from '@/types/schemas';
import type { PhieuNhapKho } from '@/types/schemas';

export interface FormFieldState {
  // Customer information
  khachHang: KhachHang | null;
  nhapXuat: NhapXuat | null;
  nhanVien: NhanVien | null;
  hanThanhToan: HanThanhToan | null;

  // Document information
  quyenChungTu: QuyenChungTu | null;
  ngoaiTe: NgoaiTe | null;

  // Other information
  diaChi: DiaChi | null;
  phuongTienVanChuyen: PhuongTienVanChuyen | null;
  phuongTienGiaoHang: PhuongThucGiaoHang | null;
  phuongThucThanhToan: PhuongThucThanhToan | null;

  // So chung tu
  soChungTu: string;
}

export interface FormFieldActions {
  // Search field setters
  setKhachHang: (khachHang: KhachHang) => void;
  setNhapXuat: (nhapXuat: NhapXuat) => void;
  setNhanVien: (nhanVien: NhanVien) => void;
  setHanThanhToan: (hanThanhToan: HanThanhToan) => void;
  setQuyenChungTu: (quyenChungTu: QuyenChungTu) => void;
  setNgoaiTe: (ngoaiTe: NgoaiTe) => void;

  setDiaChi: (diaChi: DiaChi) => void;
  setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => void;
  setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => void;
  setPhuongThucThanhToan: (phuongThucThanhToan: PhuongThucThanhToan) => void;

  // So chung tu
  setSoChungTu: (soChungTu: string) => void;

  // Utility functions
  resetState: () => void;
  updateState: (updates: Partial<FormFieldState>) => void;
}

export interface UseFormFieldStateReturn {
  state: FormFieldState;
  actions: FormFieldActions;
}

const initialState: FormFieldState = {
  // Customer information
  khachHang: null,
  nhapXuat: null,
  nhanVien: null,
  hanThanhToan: null,

  // Document information
  quyenChungTu: null,
  ngoaiTe: null,

  // Other information
  diaChi: null,
  phuongTienVanChuyen: null,
  phuongTienGiaoHang: null,
  phuongThucThanhToan: null,

  // So chung tu
  soChungTu: ''
};

function transformInitialData(initialData?: Partial<PhieuNhapKho> | null): Partial<FormFieldState> {
  if (!initialData) return {};

  return {
    // Customer information
    khachHang: initialData.ma_kh_data || null,
    nhapXuat: initialData.ma_nx_data || null,
    nhanVien: initialData.ma_nv_data || null,
    hanThanhToan: initialData.han_tt_data || null,

    // Document information
    quyenChungTu: initialData.so_ct_data || null,
    ngoaiTe: initialData.ma_nt_data || null,

    // Other information
    diaChi: initialData.ma_dc_data || null,
    phuongTienVanChuyen: initialData.ma_ptvc_data || null,
    phuongTienGiaoHang: initialData.ma_ptgh_data || null,
    phuongThucThanhToan: initialData.ma_pttt_data || null,

    // So chung tu
    soChungTu: initialData.so_ct || ''
  };
}

export function useFormFieldState(initialData?: Partial<PhieuNhapKho> | null): UseFormFieldStateReturn {
  const [state, setState] = useState<FormFieldState>({
    ...initialState,
    ...transformInitialData(initialData)
  });

  const actions: FormFieldActions = {
    // Search field setters
    setKhachHang: (khachHang: KhachHang) => {
      setState(prev => ({
        ...prev,
        khachHang
      }));
    },

    setNhapXuat: (nhapXuat: NhapXuat) => {
      setState(prev => ({
        ...prev,
        nhapXuat
      }));
    },

    setNhanVien: (nhanVien: NhanVien) => {
      setState(prev => ({
        ...prev,
        nhanVien
      }));
    },

    setHanThanhToan: (hanThanhToan: HanThanhToan) => {
      setState(prev => ({
        ...prev,
        hanThanhToan
      }));
    },

    setQuyenChungTu: (quyenChungTu: QuyenChungTu) => {
      setState(prev => ({
        ...prev,
        quyenChungTu
      }));
    },

    setNgoaiTe: (ngoaiTe: NgoaiTe) => {
      setState(prev => ({
        ...prev,
        ngoaiTe
      }));
    },

    setDiaChi: (diaChi: DiaChi) => {
      setState(prev => ({
        ...prev,
        diaChi
      }));
    },

    setPhuongTienVanChuyen: (phuongTienVanChuyen: PhuongTienVanChuyen) => {
      setState(prev => ({
        ...prev,
        phuongTienVanChuyen
      }));
    },

    setPhuongTienGiaoHang: (phuongTienGiaoHang: PhuongThucGiaoHang) => {
      setState(prev => ({
        ...prev,
        phuongTienGiaoHang
      }));
    },

    setPhuongThucThanhToan: (phuongThucThanhToan: PhuongThucThanhToan) => {
      setState(prev => ({
        ...prev,
        phuongThucThanhToan
      }));
    },

    // So chung tu
    setSoChungTu: (soChungTu: string) => {
      setState(prev => ({ ...prev, soChungTu }));
    },

    resetState: () => {
      setState(initialState);
    },

    updateState: (updates: Partial<FormFieldState>) => {
      setState(prev => ({ ...prev, ...updates }));
    }
  };

  return { state, actions };
}
